import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, RingChart, CTAButton, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { isNum } from '../shared/format';
import TranslateActionsSheet from './TranslateActionsSheet';

/**
 * Module 1 — Strategic Focus banner (A1.7), merged design.
 *
 * Per spec [A1.1]–[A1.7] live on ONE screen: this banner sits at the top of the
 * dashboard. It combines the detailed A1.7 framing (human sentence, ring, point
 * gap −Xpp, "~N more creators need to hit") with the "Translate into actions"
 * CTA (A1.8 → sheet, the only step that navigates away).
 *
 * "Creators needed" population:
 *   - roster-quality metrics → total_creators_mine
 *   - new-creator-health metrics → total_new_creators_mtd
 */
const METRIC_CFG = {
  valid_go_live_rate: { labelKey: ['tcn.m1.metric.validGoLive', 'Valid go-LIVE rate'], fragKey: ['tcn.m1.focusDetail.frag.validGoLive', 'of your creators meet the valid go-LIVE rate'], pop: 'creators' },
  rank_up_eligibility_rate_7d15h: { labelKey: ['tcn.m1.metric.rankUpEligibility', 'Rank-up eligibility (7D/15H)'], fragKey: ['tcn.m1.focusDetail.frag.rankUp', 'of your creators are rank-up eligible (7D/15H)'], pop: 'creators' },
  pk_penetration: { labelKey: ['tcn.m1.metric.pkPenetration', 'PK penetration'], fragKey: ['tcn.m1.focusDetail.frag.pkPenetration', 'of your creators did at least one PK'], pop: 'creators' },
  t14_ratio: { labelKey: ['tcn.m1.metric.t14', 'T+14 ratio'], fragKey: ['tcn.m1.focusDetail.frag.t14', 'of new creators hit ≥5 valid LIVE days in 14d'], pop: 'new' },
  go_live_rate_15d_45h_first30: { labelKey: ['tcn.m1.metric.goLive15d45h', 'Go-LIVE 15d/45h (first 30)'], fragKey: ['tcn.m1.focusDetail.frag.goLive15d45h', 'of new creators hit 15D/45H in first 30d'], pop: 'new' },
  first_30d_pk_count_20_ratio: { labelKey: ['tcn.m1.metric.first30PK', 'First 30d PK ≥ 20'], fragKey: ['tcn.m1.focusDetail.frag.pk20', 'of new creators reached ≥20 PK in first 30d'], pop: 'new' },
  diamonds_completion_pct_vs_monthly: { labelKey: ['tcn.m1.metric.diamonds', 'Monthly diamonds'], fragKey: ['tcn.m1.focusDetail.frag.diamonds', 'of your monthly diamonds target reached'], pop: null },
  recruited_creators_completion_pct_vs_monthly: { labelKey: ['tcn.m1.metric.recruited', 'Recruited creators'], fragKey: ['tcn.m1.focusDetail.frag.recruited', 'of your recruited-creators target reached'], pop: null },
};

const StrategicFocusBanner = ({ data, isDesktop = false }) => {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const metric = data?.biggest_gap_metric;
  const cfg = metric ? METRIC_CFG[metric] : null;

  if (!metric || !cfg) {
    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 4 }}>
          <Icon name="check" size={20} color={TCN.green} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: TCN.text }}>
              {t('tcn.m1.focusDetail.allOnTrack', 'All metrics on track')}
            </div>
            <div style={{ fontSize: 13, color: TCN.textDim }}>
              {t('tcn.m1.focusDetail.allOnTrackSub', 'No gap to close right now. Keep it up.')}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const metricLabel = t(...cfg.labelKey);
  const actual = isNum(data.biggest_gap_actual) ? data.biggest_gap_actual : 0;
  const target = isNum(data.biggest_gap_target) ? data.biggest_gap_target : 0;
  const gapPp = Math.round(target - actual);
  const popValue = cfg.pop === 'new'
    ? Number(data.total_new_creators_mtd ?? 0)
    : cfg.pop === 'creators'
      ? Number(data.total_creators_mine ?? 0)
      : 0;
  const creatorsNeeded = cfg.pop && popValue > 0 && gapPp > 0
    ? Math.ceil((gapPp / 100) * popValue)
    : null;

  return (
    <>
      <Card style={{ background: 'linear-gradient(160deg, #2a1118, #160a10)', border: `1px solid ${TCN.magenta}66` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: TCN.magenta,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="target" size={16} color="#000" />
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.6px',
              color: TCN.magenta,
              textTransform: 'uppercase',
            }}
          >
            {t('tcn.m1.focusDetail.priority', "Today's #1 priority")}
          </span>
        </div>

        <div style={{ fontSize: isDesktop ? 24 : 22, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.4px', color: TCN.text }}>
          {t('tcn.m1.focusDetail.title', '{{metric}} is your biggest gap.', { metric: metricLabel })}
        </div>

        <div style={{ marginTop: 8, fontSize: 13, color: TCN.textDim, lineHeight: 1.5 }}>
          {t('tcn.m1.focusDetail.sentence', '{{actual}}% {{frag}}. You need {{target}}%.', {
            actual: Math.round(actual),
            target: Math.round(target),
            frag: t(...cfg.fragKey),
          })}
        </div>

        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
          <RingChart value={actual} target={target} size={92} stroke={8} color={TCN.magenta} label={`${Math.round(actual)}%`} />
          <div>
            <div
              style={{
                fontSize: 11,
                color: TCN.textMute,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              }}
            >
              {t('tcn.m1.focusDetail.gap', 'Gap')}
            </div>
            <div
              dir="ltr"
              style={{ fontSize: 26, fontWeight: 700, color: TCN.magenta, fontVariantNumeric: 'tabular-nums' }}
            >
              −{Math.abs(gapPp)}
              {t('tcn.m1.otherGaps.pp', 'pp')}
            </div>
            {creatorsNeeded != null && (
              <div style={{ fontSize: 12, color: TCN.textDim, marginTop: 2 }}>
                {t('tcn.m1.focusDetail.creatorsNeeded', '~{{count}} more creators need to hit', { count: creatorsNeeded })}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16, maxWidth: isDesktop ? 300 : '100%' }}>
          <CTAButton
            variant="primary"
            size="md"
            onClick={() => setSheetOpen(true)}
            fullWidth
            trailing={<Icon name="arrow-right" size={16} />}
          >
            {t('tcn.m1.strategic.cta', 'Translate into actions')}
          </CTAButton>
        </div>
      </Card>

      <TranslateActionsSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        metricLabel={metricLabel}
        sourceText={`${Math.round(actual)}% / ${Math.round(target)}%`}
        gapMetric={metric}
      />
    </>
  );
};

export default StrategicFocusBanner;
